import React, { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Loader from "../../../components/ui/Loader";
import Select, { type SelectOption } from "../../../components/ui/Select";
import { useGetProfileQuery } from "../../../store/api/authApi";
import { useGetMyLeaseQuery } from "../../../store/api/leasesApi";
import {
	useCreateMaintenanceRequestMutation,
	useListMaintenanceRequestsQuery,
} from "../../../store/api/maintenanceApi";

const PRIORITY_OPTIONS: SelectOption[] = [
	{ value: "low", label: "Low" },
	{ value: "medium", label: "Medium" },
	{ value: "high", label: "High" },
	{ value: "urgent", label: "Urgent" },
];

const MaintenanceSection: React.FC = () => {
	const { data: userProfile } = useGetProfileQuery();
	// Get tenant's lease to find their property
	const { data: leaseData } = useGetMyLeaseQuery();
	const {
		data: maintenanceData,
		isLoading,
		refetch,
	} = useListMaintenanceRequestsQuery({
		page: 1,
		page_size: 50,
	});
	const [createMaintenance, { isLoading: isSubmitting }] =
		useCreateMaintenanceRequestMutation();

	const [showRequestForm, setShowRequestForm] = useState(false);
	const [formData, setFormData] = useState({
		property: "",
		title: "",
		description: "",
		priority: "medium" as const,
		location: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Backend already filters by tenant, so all results are tenant's requests
	const requests = maintenanceData?.results || [];
	const pendingRequests = requests.filter((r) => r.status === "pending");
	const inProgressRequests = requests.filter((r) => r.status === "in_progress");
	const completedRequests = requests.filter((r) => r.status === "completed");

	const formatDate = (dateString?: string) => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.property) newErrors.property = "Property is required";
		if (!formData.title.trim()) newErrors.title = "Request title is required";
		if (!formData.description.trim())
			newErrors.description = "Description is required";

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmitRequest = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm() || !userProfile || !formData.property) return;

		setErrors({});

		try {
			// Include organization_id, property_id, and requested_by_id as required by type
			// Backend may override these from user context, but TypeScript requires them
			await createMaintenance({
				organization_id: userProfile.organization || "",
				property_id: formData.property,
				requested_by_id: userProfile.id || "",
				title: formData.title,
				description: formData.description,
				priority: formData.priority,
				location: formData.location || undefined,
			}).unwrap();

			setFormData({
				property: "",
				title: "",
				description: "",
				priority: "medium",
				location: "",
			});
			setShowRequestForm(false);
			setErrors({});
			refetch();
		} catch (error: any) {
			console.error("Failed to create maintenance request:", error);
			setErrors({
				submit:
					error?.data?.error ||
					error?.data?.message ||
					"Failed to submit maintenance request. Please try again.",
			});
		}
	};

	const getStatusBadge = (status: string) => {
		const baseClasses = "px-2 py-1 text-xs font-medium rounded-full capitalize";
		switch (status) {
			case "completed":
				return `${baseClasses} bg-success/10 text-success`;
			case "in_progress":
				return `${baseClasses} bg-primary/10 text-primary`;
			case "pending":
				return `${baseClasses} bg-warning/10 text-warning`;
			case "cancelled":
				return `${baseClasses} bg-muted text-muted-foreground`;
			default:
				return `${baseClasses} bg-muted text-muted-foreground`;
		}
	};

	const getPriorityBadge = (priority: string) => {
		const baseClasses = "px-2 py-1 text-xs font-medium rounded-full capitalize";
		switch (priority) {
			case "urgent":
				return `${baseClasses} bg-error/10 text-error`;
			case "high":
				return `${baseClasses} bg-warning/10 text-warning`;
			case "medium":
				return `${baseClasses} bg-primary/10 text-primary`;
			case "low":
				return `${baseClasses} bg-muted text-muted-foreground`;
			default:
				return `${baseClasses} bg-muted text-muted-foreground`;
		}
	};

	// Auto-select property if tenant has a lease
	useEffect(() => {
		if (leaseData && leaseData.property_id && !formData.property) {
			setFormData((prev) => ({
				...prev,
				property: leaseData.property_id,
			}));
		}
	}, [leaseData, formData.property]);

	if (isLoading) {
		return (
			<Loader
				size="md"
				showText
				text="Loading maintenance requests..."
				className="py-12"
			/>
		);
	}

	// Tenant can only submit maintenance requests for their leased property
	const propertyOptions = leaseData
		? [
				{
					value: leaseData.property_id, // property_id from lease
					label:
						leaseData.property_name ||
						leaseData.property_address ||
						"My Property",
				},
		  ]
		: [];

	return (
		<div className="space-y-6">
			{/* Quick Actions Header */}
			<div className="bg-card border border-border rounded-lg card-shadow">
				<div className="p-6">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Maintenance Requests
							</h2>
							<p className="text-muted-foreground mt-1">
								Submit and track your maintenance requests
							</p>
						</div>

						<Button
							onClick={() => setShowRequestForm(true)}
							className="flex items-center space-x-2"
						>
							<Icon name="Plus" size={16} />
							<span>New Request</span>
						</Button>
					</div>

					{/* Statistics */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
						<div className="p-4 bg-primary/5 rounded-lg">
							<div className="flex items-center space-x-2 mb-2">
								<Icon name="Clock" size={20} className="text-primary" />
								<h3 className="font-medium text-foreground">Pending</h3>
							</div>
							<p className="text-2xl font-bold text-foreground">
								{pendingRequests.length}
							</p>
						</div>

						<div className="p-4 bg-primary/5 rounded-lg">
							<div className="flex items-center space-x-2 mb-2">
								<Icon name="Wrench" size={20} className="text-primary" />
								<h3 className="font-medium text-foreground">In Progress</h3>
							</div>
							<p className="text-2xl font-bold text-foreground">
								{inProgressRequests.length}
							</p>
						</div>

						<div className="p-4 bg-success/5 rounded-lg">
							<div className="flex items-center space-x-2 mb-2">
								<Icon name="CheckCircle" size={20} className="text-success" />
								<h3 className="font-medium text-foreground">Completed</h3>
							</div>
							<p className="text-2xl font-bold text-foreground">
								{completedRequests.length}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Create Request Form */}
			{showRequestForm && (
				<div className="bg-card border border-border rounded-lg card-shadow">
					<div className="p-6 border-b border-border">
						<h3 className="font-semibold text-foreground">
							New Maintenance Request
						</h3>
						<p className="text-muted-foreground text-sm mt-1">
							Fill out the form below to submit a maintenance request
						</p>
					</div>

					<form onSubmit={handleSubmitRequest} className="p-6 space-y-6">
						{errors.submit && (
							<div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
								{errors.submit}
							</div>
						)}

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{leaseData ? (
								<div>
									<label className="block text-sm font-medium text-foreground mb-2">
										Property <span className="text-destructive">*</span>
									</label>
									<div className="px-3 py-2 border border-border rounded-lg bg-muted/30 text-foreground">
										{leaseData.property_name ||
											leaseData.property_address ||
											"My Property"}
									</div>
									<input
										type="hidden"
										value={leaseData.property_id}
										onChange={() => {}}
									/>
									{errors.property && (
										<p className="text-sm text-destructive mt-1">
											{errors.property}
										</p>
									)}
								</div>
							) : (
								<div>
									<label className="block text-sm font-medium text-foreground mb-2">
										Property <span className="text-destructive">*</span>
									</label>
									<div className="px-3 py-2 border border-border rounded-lg bg-muted/30 text-muted-foreground">
										No active lease found. Please contact management.
									</div>
									{errors.property && (
										<p className="text-sm text-destructive mt-1">
											{errors.property}
										</p>
									)}
								</div>
							)}

							<Select
								label="Priority"
								options={PRIORITY_OPTIONS}
								value={formData.priority}
								onValueChange={(value) =>
									handleInputChange("priority", String(value))
								}
							/>
						</div>

						<Input
							label="Title"
							type="text"
							placeholder="Brief description of the issue"
							value={formData.title}
							onChange={(e) => handleInputChange("title", e.target.value)}
							error={errors.title}
							required
						/>

						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Description <span className="text-destructive">*</span>
							</label>
							<textarea
								rows={4}
								placeholder="Detailed description of the maintenance issue..."
								value={formData.description}
								onChange={(e) =>
									handleInputChange("description", e.target.value)
								}
								className={`w-full px-3 py-2 border rounded-lg text-foreground bg-background placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none ${
									errors.description ? "border-destructive" : "border-border"
								}`}
								required
							/>
							{errors.description && (
								<p className="text-sm text-destructive mt-1">
									{errors.description}
								</p>
							)}
						</div>

						<Input
							label="Location (Optional)"
							type="text"
							placeholder="e.g., Kitchen, Bathroom, Living Room"
							value={formData.location}
							onChange={(e) => handleInputChange("location", e.target.value)}
						/>

						<div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setShowRequestForm(false);
									setFormData({
										property: "",
										title: "",
										description: "",
										priority: "medium",
										location: "",
									});
									setErrors({});
								}}
								disabled={isSubmitting}
							>
								Cancel
							</Button>

							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? (
									<>
										<Icon
											name="Loader2"
											size={16}
											className="mr-2 animate-spin"
										/>
										Submitting...
									</>
								) : (
									<>
										<Icon name="Send" size={16} className="mr-2" />
										Submit Request
									</>
								)}
							</Button>
						</div>
					</form>
				</div>
			)}

			{/* Request History */}
			<div className="bg-card border border-border rounded-lg card-shadow">
				<div className="p-6 border-b border-border">
					<h3 className="font-semibold text-foreground">Request History</h3>
					<p className="text-muted-foreground text-sm mt-1">
						Your maintenance request history
					</p>
				</div>

				<div className="p-6">
					{requests.length > 0 ? (
						<div className="space-y-4">
							{requests
								.sort(
									(a, b) =>
										new Date(b.requested_date).getTime() -
										new Date(a.requested_date).getTime(),
								)
								.map((request) => (
									<div
										key={request.id}
										className="flex items-center justify-between p-4 border border-border rounded-lg"
									>
										<div className="flex items-center space-x-4 flex-1">
											<div className="p-2 bg-primary/10 rounded-lg">
												<Icon
													name="Wrench"
													size={20}
													className="text-primary"
												/>
											</div>

											<div className="flex-1">
												<h4 className="font-medium text-foreground">
													{request.title}
												</h4>
												<p className="text-sm text-muted-foreground">
													{request.property_name || "N/A"}
													{request.location && ` • ${request.location}`}
												</p>
												<p className="text-xs text-muted-foreground mt-1">
													Requested: {formatDate(request.requested_date)}
													{request.assigned_to_name &&
														` • Assigned to: ${request.assigned_to_name}`}
												</p>
											</div>
										</div>

										<div className="flex items-center space-x-2">
											<span className={getPriorityBadge(request.priority)}>
												{request.priority}
											</span>
											<span className={getStatusBadge(request.status)}>
												{request.status.replace("_", " ")}
											</span>
										</div>
									</div>
								))}
						</div>
					) : (
						<div className="text-center py-8">
							<Icon
								name="Wrench"
								size={48}
								className="mx-auto mb-4 text-muted-foreground opacity-50"
							/>
							<h4 className="font-medium text-foreground mb-2">
								No maintenance requests
							</h4>
							<p className="text-muted-foreground mb-6">
								You haven't submitted any maintenance requests yet.
							</p>
							<Button
								onClick={() => setShowRequestForm(true)}
								iconName="Plus"
								iconPosition="left"
							>
								Create First Request
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default MaintenanceSection;
